/**
 * keep-alive 页面：跳转详情/活动列表等子页面前保存滚动快照，返回后恢复。
 * 支持：window 纵向滚动、区块横向 scrollLeft、van-swipe 页码。
 */

const STORAGE_PREFIX = 'ecms:pageView:';

export const PAGE_KEYS = {
  HOME: 'Home',
  CAKA_HOME: 'CakaHome',
  ACTIVITY_GOODS_LIST: 'ActivityGoodsList',
};

export const SECTION_KEYS = {
  NAV_SWIPE: 'navSwipe',
  ACTIVITY_TOP: 'activityTop',
  ACTIVITY_BOTTOM: 'activityBottom',
  NAV_GRID: 'navGrid',
  CATEGORY_TAB: 'categoryTab',
};

/** 跳转子页时是否应保留当前页滚动/区块状态 */
export function shouldPreserveViewState(toPath) {
  return /^\/Details/i.test(toPath)
    || toPath === '/ActivityGoodsList'
    || toPath === '/SearchGoods'
    || toPath === '/GoodsList';
}

export function getWindowScrollTop() {
  return window.pageYOffset
    || document.documentElement.scrollTop
    || document.body.scrollTop
    || 0;
}

export function setWindowScrollTop(scrollTop) {
  if (scrollTop == null || scrollTop <= 0) return;
  document.documentElement.scrollTop = scrollTop;
  document.body.scrollTop = scrollTop;
  window.scrollTo(0, scrollTop);
}

/** 布局未就绪时（尤其 Android 整页返回）多次尝试恢复纵向滚动 */
export function scheduleWindowScrollRestore(scrollTop, delays = [0, 80, 200, 400]) {
  if (scrollTop == null || scrollTop <= 0) return;
  delays.forEach((ms) => {
    setTimeout(() => setWindowScrollTop(scrollTop), ms);
  });
}

/** 写入页面快照（pending=true 表示待恢复） */
export function savePageViewState(pageKey, snapshot) {
  if (!pageKey) return;
  try {
    sessionStorage.setItem(`${STORAGE_PREFIX}${pageKey}`, JSON.stringify({
      pending: true,
      scrollTop: 0,
      sections: {},
      ...snapshot,
    }));
  } catch (e) {
    /* ignore */
  }
}

/** 读取快照，不清除 */
export function peekPageViewState(pageKey) {
  if (!pageKey) return null;
  try {
    const raw = sessionStorage.getItem(`${STORAGE_PREFIX}${pageKey}`);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
}

/** 是否存在待恢复的快照 */
export function isPageViewRestorePending(pageKey) {
  const state = peekPageViewState(pageKey);
  return !!(state && state.pending);
}

/** 读取并清除快照 */
export function consumePageViewState(pageKey) {
  const state = peekPageViewState(pageKey);
  if (state) {
    try {
      sessionStorage.removeItem(`${STORAGE_PREFIX}${pageKey}`);
    } catch (e) {
      /* ignore */
    }
  }
  return state;
}

export function readHorizontalScroll(el) {
  if (!el) return 0;
  return el.scrollLeft || 0;
}

export function applyHorizontalScroll(el, scrollLeft) {
  if (!el || scrollLeft == null || scrollLeft < 0) return;
  el.scrollLeft = scrollLeft;
}

/**
 * 恢复页面快照：纵向滚动 + sections 回调
 * @param {string} pageKey
 * @param {Record<string, (scrollLeft: number) => void>} sectionHandlers sectionKey -> setter
 */
export function restorePageViewState(pageKey, sectionHandlers = {}) {
  const state = consumePageViewState(pageKey);
  if (!state || !state.pending) return null;

  scheduleWindowScrollRestore(state.scrollTop);

  const sections = state.sections || {};
  Object.keys(sectionHandlers).forEach((key) => {
    const handler = sectionHandlers[key];
    if (typeof handler === 'function' && sections[key] != null) {
      handler(sections[key]);
    }
  });

  return state;
}
